import {
  ID,
  ListPaginationParams,
  ListPaginationResponse,
} from '../types/common';
import { CountriesEnum } from '../types/Countries';
import { BrandsEnum } from '../types/Brands';
import { DealTypes } from '../deals';

namespace DiscountTypes {
  export enum AppliesToEnum {
    // AllProducts = 'All products',
    SpecificCollections = 'Specific collections',
    SpecificProducts = 'Specific products',
  }

  export enum CustomerEligibilityEnum {
    Everyone = 'Everyone',
    SpecificCustomerSegments = 'Specific customer segments',
    SpecificCustomers = 'Specific customers',
  }

  export enum DiscountTypeEnum {
    Percentage = 'Percentage',
    FixedAmount = 'Fixed amount',
  }

  export enum DiscountAmountTypeEnum {
    ProductDiscount = 'Product discount',
    OrderDiscount = 'Order discount',
  }

  export enum MinimumRequirementsEnum {
    None = 'None',
    MinimumPurchaseAmount = 'Minimum purchase amount',
    MinimumQuantity = 'Minimum quantity',
  }

  export enum ShopifyStatusEnum {
    Active = 'ACTIVE',
    Scheduled = 'SCHEDULED',
    Expired = 'EXPIRED',
  }

  export enum BasisEnum {
    price = 'price',
    compareAt = 'compare_at',
  }

  export enum DetailsEnum {
    PercentageDiscount = 'Percentage discount',
    PercentageDiscountAndFreeGift = 'Percentage discount and free gift',
    PercentageDiscountAndFreeShipping = 'Percentage discount and free shipping',
    SplitDiscounts = 'Split discounts',
    TieredDiscounts = 'Tiered discounts',
    ValueDiscount = 'Value discount',
    ValueDiscountAndFreeGift = 'Value discount and free gift',
    ValueDiscountAndFreeShipping = 'Value discount and free shipping',
  }

  export interface Product {
    id: string;
    title: string;
    image?: string;
  }

  export type Collection = Product;

  export interface Customer {
    id: string;
    name: string;
  }

  export interface TierItem {
    percent_off: number;
    qty_threshold: number;
    percent_off_error: null;
    threshold_error: null;
  }

  export interface SelectedCollectionParams {
    selectedCollectionIds?: string[];
    selectCollection?: {
      id: string;
      title: string;
      productsCount?: number;
      image?: string;
    }[];
  }

  export interface BuyOneGetOneDiscountParams extends SelectedCollectionParams {
    percent_off_on_collection: number;
    buy_x: number;
    get_y: number;
    incremental_buy_x_get_y: boolean;
    mixmatch: boolean;
  }

  export interface PrefixDiscountParams {
    prefix: string;
    message: string;
    mixmatch: boolean;
    basis: BasisEnum;

    // if shipping discount is enabled
    title?: string;
    minimumSubtotal?: number;
    percentOffOnShipping?: number;
  }

  export interface CartValueTier {
    cart_value_threshold: number;
    value_type: 'amount' | 'percent';
    amount_off?: number;
    percent_off?: number;
    free_gift_enabled: boolean;
    free_gift_quantity: number;
    free_gift_collection_id: string;
    free_gift_collection?: {
      id: string;
      title: string;
      productsCount?: number;
      image?: string;
    };
  }

  export interface FreeItemDiscountParams {
    cart_value_tiers: CartValueTier[];
    basis: BasisEnum;
    mixmatch: boolean;
    selectedCollectionIds: string[];
    selectCollection?: {
      id: string;
      title: string;
      productsCount?: number;
      image?: string;
    }[];
  }

  export interface ProductAndQuantityDiscountParams
    extends SelectedCollectionParams {
    percent_off_on_collection: number;
    target_variant_ids: string[];
    products: {
      id: string;
      title: string;
      image?: string;
    }[];
    qty_tiers: TierItem[];
    no_discount_applicable_tag: string;
    mixmatch: boolean;
    basis: BasisEnum;
  }

  export interface ProductQuantityBasedDiscountParams
    extends SelectedCollectionParams {
    tiers?: TierItem[];
    basis: BasisEnum;
    message?: string;
    mix_and_match?: boolean;
  }

  export type MetafieldItemValue =
    | BuyOneGetOneDiscountParams // apply_quantity_based_free_item_discount
    | PrefixDiscountParams // prefix_based_discount_rewrite from api, locally replaced with product-discount-prefix
    | FreeItemDiscountParams // free-gift-and-cart-value-combo
    | ProductAndQuantityDiscountParams // discount_by_product_and_quantity
    | ProductQuantityBasedDiscountParams; // Product Qunatity Based Discount

  export interface MetafieldItem {
    namespace: string;
    type: 'json';
    key: 'params' | 'discount';
    value: MetafieldItemValue;
  }

  export interface ItemCreateParams {
    shop_id: string;
    brand: BrandsEnum;
    country_team: CountriesEnum;
    coupon_code: string;
    applies_to?: AppliesToEnum;
    usage_limit: number | null;
    start_date: string;
    end_date?: string | null;
    strategy: DealTypes.StrategiesEnum;
    channel: DealTypes.ChannelEnum;
    collections: Collection[];
    value: number;
    coupon_description: string;
    discount_amount_type: DiscountAmountTypeEnum;
    discount_type: DiscountTypeEnum;
    discount_details: DetailsEnum;
    percentage_description?: string;
    free_gift_product_categories: string[];
    free_gift_products: string[];
    applies_once_per_order: boolean;
    applies_once_per_customer: boolean;
    minimum_requirements: MinimumRequirementsEnum;
    minimum_value: number;
    customer_eligibility: CustomerEligibilityEnum;
    products: Product[];
    customer_segments: Customer[];
    customers_individual: Customer[];
    metafields_attributes?: MetafieldItem[];

    app_description?: string;
    app_function_id?: string;

    coupon_affix?: string;
  }

  export interface ItemData
    extends Omit<ItemCreateParams, 'shop_id' | 'metafields_attributes'> {
    _id: ID;
    shop_id: ID;
    bookmark_id?: string | null;
    title: string;
    shopify_status: ShopifyStatusEnum;
    applies_to_all_customers: boolean;
    applies_to_all_products: boolean;
    discount_id: string;
    influenser_app_channel_id: number;
    influenser_app_subchannel_id: number;
    shopify_DiscountCodeNode_id: string;
    shopify_short_id: string;
    shopify_summary: string;
    created_at: string;
    updated_at: string;
    metafields?: (MetafieldItem & { _id: ID })[];
  }

  export type ItemResponse = Promise<ItemData>;

  export type ListData = ListPaginationResponse<ItemData>;

  export type ListResponse = Promise<ListData>;

  export interface GetListParams extends ListPaginationParams {
    filters: {
      // global
      country_team?: string;
      brand?: string;
      // general
      coupon_code?: string;
      no_end_date?: boolean;
      no_strategy?: boolean;
      shopify_status?: ShopifyStatusEnum[];
      discount_type?: DiscountTypeEnum[];
      start_date?: {
        $gte: string;
        $lte: string;
      };
      strategy?: DealTypes.StrategiesEnum[];
      channel?: DealTypes.ChannelEnum[];
      show_bookmarks?: boolean;
    };
  }

  export type AutocompleteSearchItem = Pick<
    ItemData,
    | 'shop_id'
    | 'coupon_code'
    | 'coupon_affix'
    | 'coupon_description'
    | 'discount_details'
    | 'discount_id'
    | 'discount_type'
    | 'free_gift_product_categories'
    | 'free_gift_products'
    | 'value'
    | 'percentage_description'
  >;

  export type AutocompleteSearchData = AutocompleteSearchItem[];

  export type AutocompleteSearchResponse = Promise<AutocompleteSearchData>;

  export interface AutocompleteSearchParams {
    shop_id?: string;
    coupon_code: string;
  }

  // ------------ start shopify types -----------------

  interface ShopifyData<T> {
    data: T;
    extensions: {
      cost: {
        requestedQueryCost: number;
        actualQueryCost: number;
        throttleStatus: {
          maximumAvailable: number;
          currentlyAvailable: number;
          restoreRate: number;
        };
      };
    };
  }

  type ShopifyResponse<T> = Promise<ShopifyData<T>>;

  interface ShopifyParams {
    country_team: CountriesEnum;
    brand: BrandsEnum;
    shop_id: string;
  }

  export interface ShopifyProductsItem {
    id: string;
    title: string;
    images: {
      edges: {
        node: {
          id: string;
          url: string;
        };
      }[];
    };
    variants: {
      edges: {
        node: {
          id: string;
          title: string;
          image: null;
        };
      }[];
    };
  }

  export interface ShopifyProductsData {
    products: {
      edges: {
        node: ShopifyProductsItem;
      }[];
    };
  }

  export type ShopifyProductsResponse = ShopifyResponse<ShopifyProductsData>;

  export interface ShopifyProductsParams extends ShopifyParams {
    product_title: string;
  }

  export interface ShopifyCollectionsItem {
    id: string;
    title: string;
    image: {
      id: string;
      url: string;
    };
  }

  export interface ShopifyCollectionsData {
    collections: {
      edges: {
        node: ShopifyCollectionsItem;
      }[];
    };
  }

  export type ShopifyCollectionsResponse =
    ShopifyResponse<ShopifyCollectionsData>;

  export interface ShopifyCollectionsParams extends ShopifyParams {
    collection_title: string;
  }

  export interface ShopifyCustomerSegmentsItem {
    id: string;
    name: string;
  }

  export interface ShopifyCustomerSegmentsData {
    segments: {
      edges: {
        node: ShopifyCustomerSegmentsItem;
      }[];
    };
  }

  export type ShopifyCustomerSegmentsResponse =
    ShopifyResponse<ShopifyCustomerSegmentsData>;

  export interface ShopifyCustomerSegmentsParams extends ShopifyParams {
    customer_list_name: string;
  }

  export interface ShopifyCustomerEmailsItem {
    id: string;
    displayName: string;
    email: string;
  }

  export interface ShopifyCustomerEmailsData {
    customers: {
      edges: {
        node: ShopifyCustomerEmailsItem;
      }[];
    };
  }

  export type ShopifyCustomerEmailsResponse =
    ShopifyResponse<ShopifyCustomerEmailsData>;

  export interface ShopifyCustomerEmailsParams extends ShopifyParams {
    customer_email: string;
  }

  // ------------ end shopify types -----------------

  export interface DiscountTypeItemAPI {
    type: 'json';
    key: 'discount' | 'params';
    namespace:
      | 'free-gift-and-cart-value-combo'
      | 'discount_by_product_and_quantity'
      | 'apply_quantity_based_free_item_discount'
      | 'prefix_based_discount_rewrite'
      | 'Product Qunatity Based Discount';
    app_description:
      | 'free-gift-and-cart-value-combo'
      | 'discount_by_product_and_quantity'
      | 'apply_quantity_based_free_item_discount'
      | 'prefix_based_discount_rewrite'
      | 'Product Qunatity Based Discount'
      | string;
    app_function_id: string;
  }

  export interface DiscountTypeItem {
    type: 'json';
    key: 'discount' | 'params';
    namespace:
      | 'free-gift-and-cart-value-combo'
      | 'discount_by_product_and_quantity'
      | 'apply_quantity_based_free_item_discount'
      | 'product-discount-prefix'
      | 'Product Qunatity Based Discount';
    app_description:
      | 'free-gift-and-cart-value-combo'
      | 'discount_by_product_and_quantity'
      | 'apply_quantity_based_free_item_discount'
      | 'product-discount-prefix'
      | 'Product Qunatity Based Discount'
      | string;
    app_function_id: string;
  }

  export type DiscountTypeItems = DiscountTypeItem[];

  export type DiscountTypeResponse = Promise<DiscountTypeItems>;
}

export default DiscountTypes;
